<template>
    <SpinnerContainer v-if="isLoading" />
    <component
        v-else-if="content"
        :is="isXJackpot ? UsingXJackpotDetails : 'div'"
        :getXJackpotCounterElements="findXJackpotElements"
        class="flex w-full"
    >
        <div class="flex w-full">
            <div class="static-page__container">
                <div class="static-page__content">
                    <Wiki
                        :wikiResponse="content"
                        :htmlBlockIFrameHostWhitelist="iFrameHostWhitelist"
                        class="article-block"
                    />
                </div>
            </div>
        </div>
    </component>
</template>

<script>
    import { getConfig } from '@superbet-group/web.lib.config';
    import { Wiki } from '@superbet-group/web.lib.ui-components';
    import { isMobileLayout } from '@superbet-group/web.lib.vue-utilities';
    import { mapActions } from 'vuex';

    import { RouteName } from '@app/router/constants/RouteName';
    import { setDocumentTitle } from '@app/utils/metaTags';
    import { wikiService } from '@core/services/wiki/WikiService';
    import sanitizeQuerySelector from '@core/utils/url/sanitizeQuerySelector';
    import { errorGuard } from '@utils/ErrorGuard';
    import { getFederationComponent } from '@src/federations/getFederationComponent';
    import { localeMap } from '@src/config/locale';
    import { WikiPath } from '@modules/wiki/WikiPath';
    import { getShellConfig } from '@src/config/shell/config';

    const DEFAULT_OFFSET = -80;
    const MOBILE_OFFSET = -40;

    export default {
        name: 'WikiContainer',
        data() {
            return {
                content: null,
            };
        },
        async created() {
            this.UsingXJackpotDetails = getFederationComponent(() => import('gaming/UsingXJackpotDetails'));
            this.iFrameHostWhitelist = getShellConfig().remoteConfig.wiki.wikiRawHtmlIFrameHostWhitelist;
            await this.fetchPage();
            this.scrollToPosition();
            this.findZendeskChatElements();
        },
        computed: {
            isXJackpot() {
                return this.$route.params.pageSlug === 'xjackpot';
            },
            pageSlug() {
                const pageUrl = (this.$route.name === RouteName.BonusAndComms.PromotionHub
                    || this.$route.name === RouteName.Free2Play.FreeGamesInfo)
                    ? this.$route.path.replace('/', '')
                    : this.$route.params.pageSlug;
                return `${pageUrl}${this.$route.hash}`;
            },
            isLoading() {
                return this.content?.kind === 'pending';
            },
        },
        watch: {
            async $route() {
                await this.fetchPage();
                this.scrollToPosition();
            },
            content(value, oldValue) {
                if (value && value.title && value.title !== oldValue.title) {
                    setDocumentTitle(value.title);
                }
            },
        },
        methods: {
            ...mapActions('ui/help', [
                'showHelp',
            ]),
            async fetchPage() {
                this.content = {
                    kind: 'pending',
                };
                await errorGuard(
                    async () => {
                        this.content = await wikiService.getWiki(this.pageSlug, this.getLocale());
                    },
                    {
                        onError: () => {
                            this.$router.push(
                                {
                                    name: RouteName.App.NotFound,
                                },
                            );
                        },
                    });
                this.initCookiePolicy();
            },
            // IMPORTANT: scrollTo uses same selectors as css, we use '#' (prefix for id)
            scrollToPosition() {
                if (this.$route.hash) {
                    let offset = DEFAULT_OFFSET;
                    if (isMobileLayout()) {
                        offset += MOBILE_OFFSET;
                    }
                    this.$scrollTo(sanitizeQuerySelector(this.$route.hash), { offset });
                }
            },
            findXJackpotElements() {
                return [
                    document.querySelector('.xjackpot-imgbox'),
                    document.querySelector('.xjackpot-amount-content'),
                ];
            },
            findZendeskChatElements() {
                const zdElement = document.querySelector('.zendesk-chat');
                if (zdElement) {
                    zdElement.onclick = () => this.showHelp();
                }
            },
            /**
             * Wiki page is loaded after OneTrust is initialized so we need to manually trigger initialization.
             * This is workaround until OneTrust fixes this on their side.
             */
            initCookiePolicy() {
                if (
                    window.OneTrust
                    && this.$route.params.pageSlug?.includes(WikiPath.CookiePolicy)
                ) {
                    let otConsentSdk = document.getElementById('onetrust-consent-sdk');
                    if (otConsentSdk) {
                        otConsentSdk.remove();
                    }

                    if (window.OneTrust != null) {
                        window.OneTrust.Init();

                        setTimeout(() => {
                            window.OneTrust.LoadBanner();

                            let toggleDisplay = document.getElementsByClassName(
                                'ot-sdk-show-settings',
                            );

                            for (let i = 0; i < toggleDisplay.length; i++) {
                                toggleDisplay[i].onclick = function (event) {
                                    event.stopImmediatePropagation();
                                    window.OneTrust.ToggleInfoDisplay();
                                };
                            }
                        }, 1000);
                    }
                }
            },
            getLocale() {
                // query locale is because native apps still use web for the wiki source of truth. and this must be a query, and
                // not the path because we have bugs on native webview.
                return this.$route.name === RouteName.Native.Wiki && this.$route.query.locale
                    ? localeMap[this.$route.query.locale]
                    : getConfig().appLocale;
            },
        },
        components: {
            Wiki,
        },
    };
</script>
